import React, { useState, useEffect } from 'react';
import { X, Search, Globe2, Calendar, Tag, Eye, Trash2, AlertTriangle } from 'lucide-react';
import { siteTemplatesApi } from '../api/client';
import type { SiteTemplate, SearchSiteTemplatesRequest } from '../schemas/validation';
import { cn } from '../utils/cn';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import { TemplateMiniPreview } from './TemplateMiniPreview';

// Mock templates for development/demo purposes
const mockTemplates: SiteTemplate[] = [
  {
    id: 'template-1',
    userId: 'user-1',
    isSystem: false,
    name: 'Business Landing Page',
    description:
      'A professional business landing page with hero section, features, testimonials, and contact form.',
    tags: 'business, landing, professional',
    isPublic: true,
    usageCount: 24,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
    siteData: {
      name: 'Business Landing Page',
      pages: [
        { title: 'Home', slug: 'home', components: '{}', orderPosition: 0 },
        { title: 'About', slug: 'about', components: '{}', orderPosition: 1 },
        { title: 'Services', slug: 'services', components: '{}', orderPosition: 2 },
        { title: 'Contact', slug: 'contact', components: '{}', orderPosition: 3 },
      ],
    },
  },
  {
    id: 'template-2',
    userId: 'user-2',
    isSystem: false,
    name: 'Portfolio Website',
    description:
      'A clean and modern portfolio website perfect for showcasing your work and projects.',
    tags: 'portfolio, creative, modern',
    isPublic: true,
    usageCount: 18,
    createdAt: '2024-01-12T14:30:00Z',
    updatedAt: '2024-01-12T14:30:00Z',
    siteData: {
      name: 'Portfolio Website',
      pages: [
        { title: 'Home', slug: 'home', components: '{}', orderPosition: 0 },
        { title: 'Portfolio', slug: 'portfolio', components: '{}', orderPosition: 1 },
        { title: 'About', slug: 'about', components: '{}', orderPosition: 2 },
        { title: 'Contact', slug: 'contact', components: '{}', orderPosition: 3 },
      ],
    },
  },
  {
    id: 'template-3',
    userId: 'user-3',
    isSystem: false,
    name: 'E-commerce Store',
    description:
      'A complete e-commerce website template with product catalog, shopping cart, and checkout.',
    tags: 'ecommerce, shop, store',
    isPublic: true,
    usageCount: 31,
    createdAt: '2024-01-10T09:15:00Z',
    updatedAt: '2024-01-10T09:15:00Z',
    siteData: {
      name: 'E-commerce Store',
      pages: [
        { title: 'Home', slug: 'home', components: '{}', orderPosition: 0 },
        { title: 'Products', slug: 'products', components: '{}', orderPosition: 1 },
        { title: 'Cart', slug: 'cart', components: '{}', orderPosition: 2 },
        { title: 'Checkout', slug: 'checkout', components: '{}', orderPosition: 3 },
        { title: 'About', slug: 'about', components: '{}', orderPosition: 4 },
      ],
    },
  },
  {
    id: 'template-4',
    userId: 'user-1',
    isSystem: false,
    name: 'Restaurant Website',
    description:
      'A delicious restaurant website with menu, reservations, and location information.',
    tags: 'restaurant, food, menu',
    isPublic: false,
    usageCount: 12,
    createdAt: '2024-01-08T16:45:00Z',
    updatedAt: '2024-01-08T16:45:00Z',
    siteData: {
      name: 'Restaurant Website',
      pages: [
        { title: 'Home', slug: 'home', components: '{}', orderPosition: 0 },
        { title: 'Menu', slug: 'menu', components: '{}', orderPosition: 1 },
        { title: 'Reservations', slug: 'reservations', components: '{}', orderPosition: 2 },
        { title: 'Contact', slug: 'contact', components: '{}', orderPosition: 3 },
      ],
    },
  },
];

interface SiteTemplateLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: SiteTemplate) => void;
  isAdmin?: boolean;
  currentUserId?: string;
}

export const SiteTemplateLibraryModal: React.FC<SiteTemplateLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  isAdmin = false,
  currentUserId,
}) => {
  const [templates, setTemplates] = useState<SiteTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [useMockData, setUseMockData] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<SiteTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<SiteTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reload templates when search/filter changes
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        loadTemplates();
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedTags, isOpen]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: SearchSiteTemplatesRequest = {
        includePublic: true,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (selectedTags.trim()) {
        params.tags = selectedTags.trim();
      }

      const response = await siteTemplatesApi.browseTemplates(params);
      setTemplates(response.templates || []);
      setUseMockData(false);
    } catch (err: any) {
      console.error('Failed to load site templates:', err);

      // Check if this is a 404 or network error (API not implemented yet)
      if (err.status === 404 || err.status === 500 || !err.status) {
        // Use mock data for development
        setUseMockData(true);
        const filteredMockTemplates = mockTemplates.filter(template => {
          const matchesSearch =
            !searchTerm ||
            template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.description?.toLowerCase().includes(searchTerm.toLowerCase());

          const matchesTags =
            !selectedTags || template.tags?.toLowerCase().includes(selectedTags.toLowerCase());

          return matchesSearch && matchesTags;
        });
        setTemplates(filteredMockTemplates);
        setError(null); // Clear error when using mock data
      } else {
        setError('Failed to load site templates. Please try again.');
        setTemplates([]);
        setUseMockData(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = (template: SiteTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  const handlePreviewTemplate = (e: React.MouseEvent, template: SiteTemplate) => {
    e.stopPropagation(); // Prevent card click from selecting the template
    setPreviewTemplate(template);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Check if the current user can delete a template
  const canDeleteTemplate = (template: SiteTemplate): boolean => {
    if (!currentUserId) return false;
    // User owns the template
    if (template.userId === currentUserId) return true;
    // Admin can delete public templates
    if (isAdmin && template.isPublic) return true;
    return false;
  };

  const handleDeleteClick = (e: React.MouseEvent, template: SiteTemplate) => {
    e.stopPropagation(); // Prevent card click from selecting the template
    setTemplateToDelete(template);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      setIsDeleting(true);
      await siteTemplatesApi.deleteTemplate(templateToDelete.id);
      // Remove from local state
      setTemplates(prev => prev.filter(t => t.id !== templateToDelete.id));
      setTemplateToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete template:', err);
      setError('Failed to delete template. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setTemplateToDelete(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTagsArray = (tagsString?: string): string[] => {
    if (!tagsString) return [];
    return tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'modal-backdrop',
        'flex items-center justify-center z-50 p-4',
        isOpen ? 'animate-fade-in' : ''
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          'modal-content',
          'max-w-6xl w-full max-h-screen overflow-y-auto',
          isOpen ? 'animate-slide-up' : ''
        )}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Globe2 className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Site Templates</h2>
              <p className="text-sm text-gray-500 mt-1">
                Choose a template to start with
                {useMockData && <span className="ml-2 text-blue-600 font-medium">(Demo Mode)</span>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              'p-2 text-gray-400 hover:text-gray-600 rounded-lg',
              'hover:bg-gray-100 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Demo Mode Notice */}
        {useMockData && (
          <div className="p-4 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <p className="text-sm text-blue-800">
                <strong>Demo Mode:</strong> These are sample templates to show how the feature will
                work. The actual templates will be loaded from the backend once the API is
                implemented.
              </p>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by tags..."
                  value={selectedTags}
                  onChange={e => setSelectedTags(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading site templates...</p>
              </div>
            </div>
          ) : !templates || templates.length === 0 ? (
            <div className="text-center py-12">
              <Globe2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No site templates found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || selectedTags
                  ? 'Try adjusting your search or filters.'
                  : useMockData
                    ? 'No templates match your criteria.'
                    : 'No site templates are available yet.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates &&
                templates.map(template => (
                  <div
                    key={template.id}
                    className={cn(
                      'bg-white border border-gray-200 rounded-lg overflow-hidden',
                      'hover:shadow-lg hover:border-primary-300 transition-all duration-200',
                      'cursor-pointer group'
                    )}
                    onClick={() => handleSelectTemplate(template)}
                  >
                    {/* Template Preview */}
                    <div className="aspect-video relative overflow-hidden bg-gray-50">
                      <TemplateMiniPreview template={template} />
                      {/* Action buttons */}
                      <div className="absolute top-2 right-2 flex items-center gap-2">
                        <button
                          onClick={e => handlePreviewTemplate(e, template)}
                          className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-md shadow-md',
                            'bg-primary-600 text-white font-medium text-sm',
                            'hover:bg-primary-700 transition-colors',
                            'opacity-90 group-hover:opacity-100'
                          )}
                          title="Preview template"
                        >
                          <Eye className="h-4 w-4" />
                          <span>Preview</span>
                        </button>
                        {canDeleteTemplate(template) && (
                          <button
                            onClick={e => handleDeleteClick(e, template)}
                            className={cn(
                              'flex items-center gap-1.5 px-2 py-1.5 rounded-md shadow-md',
                              'bg-red-600 text-white font-medium text-sm',
                              'hover:bg-red-700 transition-colors',
                              'opacity-90 group-hover:opacity-100'
                            )}
                            title="Delete template"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {/* Pages count indicator */}
                      <div className="absolute bottom-2 left-2">
                        <span className="bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-md">
                          {template.siteData?.pages?.length || 0} pages
                        </span>
                      </div>
                      {/* Demo badge */}
                      {useMockData && (
                        <div className="absolute top-2 left-2">
                          <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-md font-medium">
                            Demo
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Template Info */}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {template.name}
                      </h3>

                      {template.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {template.description}
                        </p>
                      )}

                      {/* Tags */}
                      {template.tags && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {getTagsArray(template.tags)
                            .slice(0, 3)
                            .map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {tag}
                              </span>
                            ))}
                          {getTagsArray(template.tags).length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{getTagsArray(template.tags).length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Template Meta */}
                      <div className="flex items-center text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(template.createdAt)}</span>
                        </div>
                      </div>

                      {/* Public indicator */}
                      {template.isPublic && (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Public Template
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-100">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>

      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          isOpen={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSelectTemplate={() => {
            handleSelectTemplate(previewTemplate);
            setPreviewTemplate(null);
          }}
          useMockData={useMockData}
        />
      )}

      {/* Delete Confirmation Modal */}
      {templateToDelete && (
        <div
          className={cn(
            'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4',
            'animate-fade-in'
          )}
          onClick={handleCancelDelete}
        >
          <div
            className={cn('bg-white rounded-xl shadow-2xl max-w-md w-full p-6', 'animate-slide-up')}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Template</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>"{templateToDelete.name}"</strong>? This
              action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <button onClick={handleCancelDelete} className="btn-secondary" disabled={isDeleting}>
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className={cn(
                  'px-4 py-2 bg-red-600 text-white font-medium rounded-lg',
                  'hover:bg-red-700 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center space-x-2'
                )}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 spinner border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
